import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ReactModal from 'react-modal';
import { Box, Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import _ from 'lodash';
import { mobilizeiAppPublicationsService } from '../_services/mobilizei/appPublications.service';
import SelectionStep from './SelectionStep';
import PrepareStoreStep from './PrepareStoreStep';
import IosPrereqStep from './IosPrereqStep';
import IosCredentialsStep from './IosCredentialsStep';
import RegisterDeviceStep from './RegisterDeviceStep';
import StoreSetupStep from './StoreSetupStep';
import ReviewStep from './ReviewStep';

ReactModal.setAppElement('#root');

const customStyles = {
    overlay: { backgroundColor: 'rgba(10, 10, 10, 0.45)', zIndex: 999 },
    content: {
        height: '85%',
        width: 'auto',
        maxWidth: '680px',
        margin: 'auto',
        overflowY: 'auto',
    },
};

const useStyles = makeStyles(() => ({
    title: { marginTop: 0 },
    subtitle: { color: '#555', margin: '0 0 16px 0', width: '100%' },
    nav: { display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 32, gap: 16 },
    stepIndicator: { color: '#888', fontSize: 13, textAlign: 'center', marginBottom: 8 },
    success: { textAlign: 'center', padding: '32px 16px' },
    successIcon: { fontSize: 48, color: '#43a047' },
    successText: { marginTop: 12, color: '#222' },
    successHint: { marginTop: 8, color: '#555' },
}));

export function deriveAutoSubmit({ mobilePlatform, appType, isNewApp }) {
    if (appType === 'testing') return false;
    if (mobilePlatform === 'ios') return true;
    return isNewApp ? false : true;
}

/** Returns true if the store is already prepared for the given platform. */
function isStorePrepared(store, mobilePlatform) {
    if (!store.slug || !store.expoProjectId || !store.expoAccountName || !store.firebaseProjectName) return false;
    if (mobilePlatform === 'android') return !!_.get(store, 'platforms.android.bundleId');
    if (mobilePlatform === 'ios') return !!_.get(store, 'platforms.ios.packageName');
    return false;
}

/** Returns true if iOS credentials are fully configured and in sync with OneSignal state. */
function isIosCredentialsReady(store) {
    const ios = _.get(store, 'platforms.ios', {});
    if (!ios.ascApiKeyId || !ios.distributionCertificate) return false;
    const oneSignalEnabled = !!_.get(store, 'integrations.oneSignalAppId');
    if (ios.credentialsOneSignalEnabled !== undefined && ios.credentialsOneSignalEnabled !== oneSignalEnabled) return false;
    return true;
}

/** Derives a suggested isNewApp value from the store's current publish status. */
function deriveIsNewApp(store, mobilePlatform) {
    const status = _.get(store, `platforms.${mobilePlatform}.publishStatus`);
    const buildVersion = _.get(store, `platforms.${mobilePlatform}.currentBuildVersion`);
    if (!status && !buildVersion) return true;
    if (status && status !== 'unpublished') return false;
    return true;
}

/**
 * Builds the ordered list of steps based on store state + operator selections.
 * Each step: { key, Component, label }
 */
function buildSteps({ store, platformId, formData, localStore, setLocalStore, onStepComplete }) {
    const { mobilePlatform, appType, isNewApp } = formData;
    const isProduction = appType === 'production';
    const isTesting = appType === 'testing';
    const isIOS = mobilePlatform === 'ios';

    const steps = [];

    // Step 1: always — selection
    steps.push({
        key: 'selection',
        label: 'Seleção',
        Component: SelectionStep,
    });

    // Only build remaining steps after selections are complete
    if (!mobilePlatform || !appType || (isProduction && isNewApp === '')) return steps;

    // Step 2: prepare — only if store not yet prepared for this platform
    if (!isStorePrepared(localStore, mobilePlatform)) {
        steps.push({
            key: 'prepare',
            label: 'Preparar loja',
            Component: PrepareStoreStep,
        });
    }

    // Step 3: iOS prerequisites — blocks if teamID/teamName are missing (required before devices and credentials)
    if (isIOS) {
        const missingTeam = !_.get(localStore, 'platforms.ios.teamID') || !_.get(localStore, 'platforms.ios.teamName');
        if (missingTeam) {
            steps.push({
                key: 'iosPrereq',
                label: 'Pré-requisitos iOS',
                Component: IosPrereqStep,
            });
            // IosPrereqStep always returns isFinished(false) when fields are missing — wizard is naturally blocked here
            return steps;
        }
    }

    // Step 4: register device — iOS + testing only, BEFORE credentials so UDIDs are included in the Ad Hoc profile
    if (isIOS && isTesting) {
        steps.push({
            key: 'iosDevices',
            label: 'Devices de teste',
            Component: RegisterDeviceStep,
        });
    }

    // Step 5: iOS credentials — iOS only; runs if creds not ready OR operator added a new device (to regenerate the Ad Hoc profile with the new UDID)
    if (isIOS && (!isIosCredentialsReady(localStore) || formData.addedNewIosDevice === true)) {
        steps.push({
            key: 'iosCredentials',
            label: 'Credenciais iOS',
            Component: IosCredentialsStep,
        });
    }

    // Step 5: store setup — production only
    if (isProduction) {
        steps.push({
            key: 'storeSetup',
            label: 'Configurar loja',
            Component: StoreSetupStep,
        });
    }

    // Step 6: review — always
    steps.push({
        key: 'review',
        label: 'Revisar e iniciar',
        Component: ReviewStep,
    });

    return steps;
}

export default function PublishWizard({ data, isOpen, onClose, onDataUpdate, platformId }) {
    const classes = useStyles();

    const [loading, setLoading] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const [isNextEnabled, setIsNextEnabled] = useState(false);
    const [dispatched, setDispatched] = useState(false);
    const [error, setError] = useState(null);

    // Local copy of the store — updated as prepare/credentials steps complete
    const [localStore, setLocalStore] = useState(data);

    const [formData, setFormData] = useState({
        mobilePlatform: '',
        appType: '',
        isNewApp: '',
        addedNewIosDevice: '',
    });

    const handleChangeData = (property, value) => {
        setFormData(prev => {
            const next = { ...prev, [property]: value };
            if (property === 'appType' && value === 'testing') next.isNewApp = '';
            // Reset device flag when platform/type changes
            if (property === 'mobilePlatform' || property === 'appType') next.addedNewIosDevice = '';
            return next;
        });
        setIsNextEnabled(false);
    };

    // Recompute steps whenever formData or localStore changes
    const steps = buildSteps({
        store: data,
        platformId,
        formData,
        localStore,
        setLocalStore,
    });

    const currentStep = steps[stepIndex];
    const isLastStep = stepIndex === steps.length - 1;

    const handlePrevious = () => { setStepIndex(i => i - 1); setIsNextEnabled(false); };
    const handleNext = () => { setStepIndex(i => i + 1); setIsNextEnabled(false); };
    const handleStepFinished = (ok) => setIsNextEnabled(ok);

    const handleDispatch = () => {
        setLoading(true);
        setError(null);
        const buildProfile = formData.appType === 'production' ? 'store-production' : 'store-staging';
        const autoSubmit = deriveAutoSubmit(formData);
        mobilizeiAppPublicationsService.startAppPublication({
            storeId: data.id,
            platformId: platformId || data.platformId,
            mobilePlatform: formData.mobilePlatform,
            buildProfile,
            autoSubmit,
            isNewApp: formData.isNewApp === true,
        })
            .then(() => {
                setDispatched(true);
            })
            .catch((e) => {
                const missingFields = e?.missingFields;
                if (missingFields?.length) {
                    setError(`Campos obrigatórios faltando:\n${missingFields.join(', ')}`);
                } else {
                    setError(`Erro ao iniciar a publicação: ${e?.message || JSON.stringify(e)}`);
                }
            })
            .finally(() => setLoading(false));
    };

    const handleClose = () => {
        if (dispatched) onDataUpdate();
        onClose();
        // Reset after close animation
        setTimeout(() => {
            setStepIndex(0);
            setIsNextEnabled(false);
            setDispatched(false);
            setError(null);
            setLocalStore(data);
            setFormData({ mobilePlatform: '', appType: '', isNewApp: '', addedNewIosDevice: '' });
        }, 300);
    };

    const stepLabel = currentStep ? currentStep.label : '';
    const stepNumber = stepIndex + 1;
    const totalSteps = steps.length;

    // Props passed to every step component
    const stepProps = {
        store: localStore,
        setLocalStore,
        platformId: platformId || data.platformId,
        formData,
        handleChangeData,
        isFinished: handleStepFinished,
    };

    return (
        <ReactModal isOpen={isOpen} onRequestClose={handleClose} style={customStyles}>
            <Box display="flex" flexDirection="column" justifyContent="space-between" style={{ height: '100%' }}>
                <Grid container>
                    <h2 className={classes.title}>Publicar aplicativo</h2>
                    <p className={classes.subtitle}>{data.title} — {data.platformId} · {data.id}</p>

                    {dispatched ? (
                        <Box className={classes.success}>
                            <Typography variant="h6" className={classes.successText}>
                                ✅ Build em andamento!
                            </Typography>
                            <Typography variant="body1" className={classes.successHint}>
                                O processo de publicação foi iniciado. Acompanhe o progresso e conclua a publicação
                                em <strong>Publicações (expo)</strong> quando o build estiver pronto (~15 min).
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {totalSteps > 1 && (
                                <Typography className={classes.stepIndicator}>
                                    Passo {stepNumber} de {totalSteps} — {stepLabel}
                                </Typography>
                            )}

                            {currentStep && (
                                <currentStep.Component {...stepProps} />
                            )}

                            {error && (
                                <Typography variant="body2" style={{ color: '#c62828', marginTop: 12 }}>
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </Grid>

                {loading ? (
                    <Box className={classes.nav}><CircularProgress /></Box>
                ) : (
                    <Box className={classes.nav}>
                        {!dispatched && stepIndex > 0 && (
                            <Button variant="outlined" onClick={handlePrevious}>Voltar</Button>
                        )}
                        {dispatched ? (
                            <Button variant="contained" color="primary" onClick={handleClose}>Fechar</Button>
                        ) : isLastStep ? (
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!isNextEnabled}
                                onClick={handleDispatch}
                            >
                                Iniciar publicação
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={!isNextEnabled}
                                onClick={handleNext}
                            >
                                Próximo
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </ReactModal>
    );
}
